import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, BoxList, Button, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'
import { PayrollResultFragment } from 'generated/resources/graphql'
import moment from 'moment'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import DeleteModal from 'modules/compensations/pages/CompensationsPayrollRun/ManagePayrollResultsModal/_deleteModal'

type PayrollResultStatus = 'pending' | 'finished' | 'completed_with_errors' | 'error'
type TagType = 'lime' | 'champagne' | 'malibu' | 'radical' | 'grey'

const STATUS_TAG_MAPPING: Record<PayrollResultStatus, TagType> = {
  finished: 'lime',
  completed_with_errors: 'champagne',
  pending: 'malibu',
  error: 'radical',
}

function isPayrollResultStatus(status: string): status is PayrollResultStatus {
  return ['pending', 'finished', 'completed_with_errors', 'error'].includes(status)
}

function getTagType(status: PayrollResultStatus | undefined): TagType {
  if (!status) return 'grey'
  return STATUS_TAG_MAPPING[status]
}

function getStatusTranslation(status: PayrollResultStatus | undefined): string {
  if (!status) return ''

  switch (status) {
    case 'pending':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.pending'
      )
    case 'error':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.error'
      )
    case 'completed_with_errors':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.completed_with_errors'
      )
    case 'finished':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.finished'
      )
  }
}

interface Props {
  result: PayrollResultFragment
  onRemove: (result: PayrollResultFragment) => void
}

const Item = ({ result, onRemove }: Props) => {
  const [open, toggle] = useToggle(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const createdAt = i18nDateToString(moment(result.createdAt), 'ui_short_with_time')

  const rawStatus = result.importProcess?.status || ''
  const status = isPayrollResultStatus(rawStatus) ? rawStatus : 'pending'
  const hasErrors = status === 'error' || status === 'completed_with_errors'
  const hasOriginalFile = result.importProcess?.originalFileUrl

  const closeModal = () => {
    setShowDeleteModal(false)
  }

  const handleCloseModal = () => {
    closeModal()
  }

  const navigateToUniversalImporter = () => {
    if (!result.importProcess?.id) return

    navigate(
      navigation.settings.universalImporter.universalImporterProcess.fullPath({
        importProcessId: result.importProcess?.id,
      })
    )
  }

  const handleDeleteResultClick = () => {
    setIsDeleting(true)
    onRemove(result)
    track(trackingEvents.compensations.deletePayrollResult, {
      payroll_run_id: result?.compensationsPayrollRunId,
      id: result?.id,
      original_file_name: result.importProcess?.originalFileFilename,
      original_file_url: result.importProcess?.originalFileUrl,
      status: result.importProcess?.status,
      errored_count: result.importProcess?.errored,
    })
  }

  const downloadOriginalFile = () => {
    if (!result.importProcess?.originalFileUrl) return

    window.open(result.importProcess?.originalFileUrl, '_blank')
  }

  return (
    <>
      <BoxList>
        <BoxList.Item>
          <Box width="full">
            <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
              <Box flexDirection="column" alignItems="flexStart" gap="s12">
                <Tag leftItem="circle" type={getTagType(status)} showCircle>
                  {getStatusTranslation(status)}
                </Tag>

                {result.importProcess?.originalFileFilename && (
                  <Text.Normal size="300" weight="semibold">
                    {result.importProcess?.originalFileFilename}
                  </Text.Normal>
                )}
                <Box flexDirection="row" gap="s8">
                  <Text.Normal size="300">
                    {i18n.t(
                      'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.actions.total',
                      {
                        amount: result.importProcess?.total,
                      }
                    )}
                    {hasErrors && ', '}
                  </Text.Normal>
                  {hasErrors && (
                    <Text.Normal size="300">
                      {i18n.t(
                        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.actions.errors',
                        {
                          errors: result.importProcess?.errored,
                        }
                      )}
                    </Text.Normal>
                  )}
                </Box>

                <Text.Normal size="200" weight="medium" color="grey700">
                  {createdAt}
                </Text.Normal>
              </Box>

              <Box flexDirection="row" alignItems="center">
                {status !== 'pending' && (
                  <PopoverMenu type="list">
                    <PopoverMenu.Trigger>
                      <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
                    </PopoverMenu.Trigger>
                    {open && (
                      <PopoverMenu.Content side="bottom" align="start" disablePortal>
                        <Box flexDirection="column">
                          {hasOriginalFile && (
                            <PopoverMenu.TouchableOption
                              icon={'download'}
                              disabled={isDeleting}
                              title={i18n.t('common.download')}
                              onClick={downloadOriginalFile}
                            />
                          )}
                          {hasErrors && (
                            <PopoverMenu.TouchableOption
                              icon={'eye-visible'}
                              disabled={isDeleting}
                              title={i18n.t(
                                'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.actions.view_details'
                              )}
                              onClick={navigateToUniversalImporter}
                            />
                          )}
                          <PopoverMenu.TouchableOption
                            icon={'delete'}
                            disabled={isDeleting}
                            title={i18n.t(
                              'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.actions.delete'
                            )}
                            onClick={() => {
                              setShowDeleteModal(true)
                              track(trackingEvents.compensations.clickDeleteButtonOnPayrollResult)
                            }}
                          />
                        </Box>
                      </PopoverMenu.Content>
                    )}
                  </PopoverMenu>
                )}
              </Box>
            </Box>
          </Box>
        </BoxList.Item>
      </BoxList>
      {showDeleteModal && (
        <DeleteModal
          result={result}
          disabled={isDeleting}
          onClose={handleCloseModal}
          onDelete={handleDeleteResultClick}
        />
      )}
    </>
  )
}

export default Item
